import { base64 } from 'rfc4648';
import fs from 'fs';
import pathUtil from 'path';

/**
 * Interprets a path as a series of folder lookups,
 * handling special components like `.` and `..`.
 */
function followPath (folder, parts) {
  var i = 0; // Read index
  var j = 0; // Write index

  // Shift down good elements, dropping bad ones:
  while (i < parts.length) {
    var part = parts[i++];
    if (part === '..') { j--; }
    else if (part !== '.' && part !== '') { parts[j++] = part; }

    if (j < 0) { throw new Error('Path would escape folder') }
  }

  // Navigate the folder:
  for (i = 0; i < j; ++i) {
    folder = folder.folder(parts[i]);
  }
  return folder
}

/**
 * Navigates down to the file indicated by the path.
 */
function locateFile (folder, path) {
  var parts = path.split('/');
  var filename = parts.pop();
  return followPath(folder, parts).file(filename)
}

/**
 * Navigates down to the sub-folder indicated by the path.
 */
function locateFolder (folder, path) {
  var parts = path.split('/');
  return followPath(folder, parts)
}

/**
 * Applies an async function to all the files in a folder.
 */
function mapFiles (folder, f) {
  return folder
    .listFiles()
    .then(function (names) { return Promise.all(names.map(function (name) { return f(folder.file(name), name, folder); })); }
    )
}

/**
 * Applies an async function to all the sub-folders in a folder.
 */
function mapFolders (folder, f) {
  return folder
    .listFolders()
    .then(function (names) { return Promise.all(names.map(function (name) { return f(folder.folder(name), name, folder); })); }
    )
}

/**
 * Recursively applies an async function to all the files in a folder tree.
 * The file names are expanded into paths, and the result is a flat array.
 */
function mapAllFiles (folder, f) {
  function recurse (folder, f, prefix) {
    return Promise.all([
      mapFiles(folder, function (file, name) { return f(file, prefix + name, folder); }),
      mapFolders(folder, function (folder, name) { return recurse(folder, f, prefix + name + '/'); }
      )
    ]).then(function (ref) {
      var files = ref[0];
      var folders = ref[1];

      return files.concat.apply(files, folders);
    })
  }

  return recurse(folder, f, '')
}

/**
 * Verifies that a name contains no slashes.
 */
function checkName (name) {
  if (typeof name !== 'string' || !name.length || /[/]/.test(name)) {
    throw new Error(("Invalid file name \"" + name + "\""))
  }
}

var Data = typeof Uint8Array !== 'undefined' ? Uint8Array : Array;

/**
 * Lists the keys in a localStorage object.
 */
function storageKeys (storage) {
  var keys = [];
  for (var i = 0; i < storage.length; ++i) {
    keys.push(storage.key(i));
  }
  return keys
}

/**
 * A single file stored in localStorage.
 */
var LocalStorageFile = function LocalStorageFile (storage, path) {
  this._storage = storage;
  this._path = path;
};

LocalStorageFile.prototype.delete = function delete$1 () {
  this._storage.removeItem(this._path);
  return Promise.resolve()
};

LocalStorageFile.prototype.getData = function getData () {
  return this.getText().then(function (text) { return base64.parse(text, { out: Data }); })
};

LocalStorageFile.prototype.getText = function getText () {
  var item = this._storage.getItem(this._path);
  return item != null
    ? Promise.resolve(item)
    : Promise.reject(new Error(("Cannot load \"" + (this._path) + "\"")))
};

LocalStorageFile.prototype.setText = function setText (text) {
  if (typeof text !== 'string') {
    return Promise.reject(new TypeError('Expected a string'))
  }

  this._storage.setItem(this._path, text);
  return Promise.resolve()
};

LocalStorageFile.prototype.setData = function setData (data) {
  return this.setText(base64.stringify(data))
};

/**
 * Emulates a filesystem inside a localStorage instance.
 */
var LocalStorageFolder = function LocalStorageFolder (storage, path) {
  this._storage = storage;
  this._path = path + '/';
};

LocalStorageFolder.prototype.delete = function delete$2 () {
    var this$1 = this;

  var test = new RegExp(("^" + (this._path)));
  storageKeys(this._storage).forEach(function (key) {
    if (test.test(key)) {
      this$1._storage.removeItem(key);
    }
  });
  return Promise.resolve()
};

LocalStorageFolder.prototype.file = function file (name) {
  checkName(name);
  return new LocalStorageFile(this._storage, this._path + name)
};

LocalStorageFolder.prototype.folder = function folder (name) {
  checkName(name);
  return new LocalStorageFolder(this._storage, this._path + name)
};

LocalStorageFolder.prototype.listFiles = function listFiles () {
  var test = new RegExp(("^" + (this._path) + "([^/]+)$"));

  var names = [];
  storageKeys(this._storage).forEach(function (key) {
    var results = test.exec(key);
    if (results != null) { names.push(results[1]); }
  });

  return Promise.resolve(names)
};

LocalStorageFolder.prototype.listFolders = function listFolders () {
  var test = new RegExp(("^" + (this._path) + "([^/]+)/.+"));

  var names = {};
  storageKeys(this._storage).forEach(function (key) {
    var results = test.exec(key);
    if (results != null) { names[results[1]] = true; }
  });

  return Promise.resolve(Object.keys(names))
};

function makeLocalStorageFolder (
  storage,
  opts
) {
  if ( storage === void 0 ) storage = window.localStorage;
  if ( opts === void 0 ) opts = {};

  return new LocalStorageFolder(storage, opts.prefix || '')
}

function logConsole (path, operation) {
  console.log((operation + " \"" + path + "\""));
}

function log (path, operation, opts) {
  var f = opts.callback != null ? opts.callback : logConsole;

  if (opts.verbose || /set|delete/.test(operation)) {
    f(path, operation);
  }
}

var LoggedFile = function LoggedFile (file, path, opts) {
  this._file = file;
  this._path = path;
  this._opts = opts;
};

LoggedFile.prototype.delete = function delete$1 () {
  log(this._path, 'delete file', this._opts);
  return this._file.delete()
};

LoggedFile.prototype.getData = function getData () {
  log(this._path, 'get data', this._opts);
  return this._file.getData()
};

LoggedFile.prototype.getText = function getText () {
  log(this._path, 'get text', this._opts);
  return this._file.getText()
};

LoggedFile.prototype.setData = function setData (data) {
  log(this._path, 'set data', this._opts);
  return this._file.setData(data)
};

LoggedFile.prototype.setText = function setText (text) {
  log(this._path, 'set text', this._opts);
  return this._file.setText(text)
};

var LoggedFolder = function LoggedFolder (folder, path, opts) {
  this._folder = folder;
  this._path = path;
  this._opts = opts;
};

LoggedFolder.prototype.delete = function delete$2 () {
  log(this._path, 'delete folder', this._opts);
  return this._folder.delete()
};

LoggedFolder.prototype.file = function file (name) {
  return new LoggedFile(
    this._folder.file(name),
    this._path + name,
    this._opts
  )
};

LoggedFolder.prototype.folder = function folder (name) {
  return new LoggedFolder(
    this._folder.folder(name),
    this._path + name + '/',
    this._opts
  )
};

LoggedFolder.prototype.listFiles = function listFiles () {
  log(this._path, 'list files', this._opts);
  return this._folder.listFiles()
};

LoggedFolder.prototype.listFolders = function listFolders () {
  log(this._path, 'list folders', this._opts);
  return this._folder.listFolders()
};

function makeLoggedFolder (folder, opts) {
  if ( opts === void 0 ) opts = {};

  return new LoggedFolder(folder, '', opts)
}

/**
 * A single file stored in memory.
 */
var MemoryFile = function MemoryFile (storage, path) {
  this._storage = storage;
  this._path = path;
};

MemoryFile.prototype.delete = function delete$1 () {
  delete this._storage[this._path];
  return Promise.resolve()
};

MemoryFile.prototype.getData = function getData () {
  var item = this._storage[this._path];
  return item != null
    ? Promise.resolve(item)
    : Promise.reject(new Error(("Cannot load \"" + (this._path) + "\"")))
};

MemoryFile.prototype.getText = function getText () {
  var item = this._storage[this._path];
  return item != null
    ? Promise.resolve(item)
    : Promise.reject(new Error(("Cannot load \"" + (this._path) + "\"")))
};

MemoryFile.prototype.setData = function setData (data) {
  this._storage[this._path] = data;
  return Promise.resolve()
};

MemoryFile.prototype.setText = function setText (text) {
  if (typeof text !== 'string') {
    return Promise.reject(new TypeError('Expected a string'))
  }

  this._storage[this._path] = text;
  return Promise.resolve()
};

/**
 * Emulates a filesystem in memory.
 */
var MemoryFolder = function MemoryFolder (storage, path) {
  this._storage = storage;
  this._path = path + '/';
};

MemoryFolder.prototype.delete = function delete$2 () {
    var this$1 = this;

  var test = new RegExp(("^" + (this._path)));
  Object.keys(this._storage).forEach(function (key) {
    if (test.test(key)) {
      delete this$1._storage[key];
    }
  });
  return Promise.resolve()
};

MemoryFolder.prototype.file = function file (name) {
  checkName(name);
  return new MemoryFile(this._storage, this._path + name)
};

MemoryFolder.prototype.folder = function folder (name) {
  checkName(name);
  return new MemoryFolder(this._storage, this._path + name)
};

MemoryFolder.prototype.listFiles = function listFiles () {
  var test = new RegExp(("^" + (this._path) + "([^/]+)$"));

  var names = [];
  Object.keys(this._storage).forEach(function (key) {
    var results = test.exec(key);
    if (results != null) { names.push(results[1]); }
  });

  return Promise.resolve(names)
};

MemoryFolder.prototype.listFolders = function listFolders () {
  var test = new RegExp(("^" + (this._path) + "([^/]+)/.+"));

  var names = {};
  Object.keys(this._storage).forEach(function (key) {
    var results = test.exec(key);
    if (results != null) { names[results[1]] = true; }
  });

  return Promise.resolve(Object.keys(names))
};

function makeMemoryFolder (storage) {
  if ( storage === void 0 ) storage = {};

  return new MemoryFolder(storage, '')
}

// Promise versions of node.js file operations: -----------------------------

function mkdir (path) {
  return new Promise(function (resolve, reject) { return fs.mkdir(
      path,
      function (err) { return (err && err.code !== 'EEXIST' ? reject(err) : resolve()); }
    ); }
  )
}

function rmdir (path) {
  return new Promise(function (resolve, reject) { return fs.rmdir(path, function (err, out) { return (err ? reject(err) : resolve(out)); }); }
  )
}

function readdir (path) {
  return new Promise(function (resolve, reject) { return fs.readdir(path, function (err, out) { return (err ? reject(err) : resolve(out)); }); }
  )
}

function readFile (path, opts) {
  return new Promise(function (resolve, reject) { return fs.readFile(path, opts, function (err, out) { return (err ? reject(err) : resolve(out)); }); }
  )
}

function stat (path) {
  return new Promise(function (resolve, reject) { return fs.stat(path, function (err, out) { return (err ? reject(err) : resolve(out)); }); }
  )
}

function unlink (path) {
  return new Promise(function (resolve, reject) { return fs.unlink(path, function (err, out) { return (err ? reject(err) : resolve(out)); }); }
  )
}

function writeFile (path, data, opts) {
  return new Promise(function (resolve, reject) { return fs.writeFile(
      path,
      data,
      opts,
      function (err, out) { return (err ? reject(err) : resolve(out)); }
    ); }
  )
}

// Helpers: -----------------------------------------------------------------

/**
 * If node.js returns a missing-file error (`ENOENT`),
 * translate that into the fallback value and proceed.
 */
function ignoreMissing (fallback) {
  return function (err) {
    if (err.code !== 'ENOENT') { throw err }
    return fallback
  }
}

/**
 * Reads a directory, returning a list of names and a list of stat objects.
 * Returns empty lists if the directory doesn't exist.
 */
function readdirStat (path) {
  return readdir(path)
    .catch(ignoreMissing([]))
    .then(function (names) { return Promise.all(
        names.map(function (name) { return stat(pathUtil.join(path, name)); })
      ).then(function (stats) { return ({ names: names, stats: stats }); }); }
    )
}

/**
 * Recursively creates a directory.
 */
function mkdirDeep (path) {
  return mkdir(path).catch(function (err) {
    if (err.code !== 'ENOENT') { throw err }
    return mkdirDeep(pathUtil.dirname(path)).then(function () { return mkdir(path); })
  })
}

/**
 * Writes a file, creating its directory if needed.
 */
function writeFileDeep (path, data, opts) {
  return writeFile(path, data, opts).catch(function (err) {
    if (err.code !== 'ENOENT') { throw err }
    return mkdirDeep(pathUtil.dirname(path)).then(function () { return writeFile(path, data, opts); }
    )
  })
}

var NodeFile = function NodeFile (path) {
  this._path = path;
};

NodeFile.prototype.delete = function delete$1 () {
  return unlink(this._path).catch(ignoreMissing())
};

NodeFile.prototype.getData = function getData () {
  return readFile(this._path, null)
};

NodeFile.prototype.getText = function getText () {
  return readFile(this._path, 'utf8')
};

NodeFile.prototype.setData = function setData (data) {
  return writeFileDeep(this._path, Uint8Array.from(data), null)
};

NodeFile.prototype.setText = function setText (text) {
  return writeFileDeep(this._path, text, 'utf8')
};

var NodeFolder = function NodeFolder (path) {
  this._path = path;
};

NodeFolder.prototype.delete = function delete$2 () {
    var this$1 = this;

  return readdirStat(this._path)
    .then(function (lists) {
      var names = lists.names;
        var stats = lists.stats;
      var files = names.filter(function (name, i) { return stats[i].isFile(); });
      var folders = names.filter(function (name, i) { return stats[i].isDirectory(); });

      // Recursively delete children:
      return Promise.all(files.map(function (name) { return this$1.file(name).delete(); }).concat( folders.map(function (name) { return this$1.folder(name).delete(); })
      ))
    })
    .then(function () { return rmdir(this$1._path); })
    .catch(ignoreMissing())
};

NodeFolder.prototype.file = function file (name) {
  checkName(name);
  return new NodeFile(pathUtil.join(this._path, name))
};

NodeFolder.prototype.folder = function folder (name) {
  checkName(name);
  return new NodeFolder(pathUtil.join(this._path, name))
};

NodeFolder.prototype.listFiles = function listFiles () {
  return readdirStat(this._path).then(function (lists) {
    var names = lists.names;
      var stats = lists.stats;
    return names.filter(function (name, i) { return stats[i].isFile(); })
  })
};

NodeFolder.prototype.listFolders = function listFolders () {
  return readdirStat(this._path).then(function (lists) {
    var names = lists.names;
      var stats = lists.stats;
    return names.filter(function (name, i) { return stats[i].isDirectory(); })
  })
};

function makeNodeFolder (path) {
  return new NodeFolder(pathUtil.resolve(path))
}

function removeDuplicates (master, fallback) {
  var blacklist = {};
  var out = [];
  master.forEach(function (item) {
    if (/\._x_$/.test(item)) {
      blacklist[item] = true;
    } else {
      blacklist[item + '._x_'] = true;
      out.push(item);
    }
  });

  fallback.forEach(function (item) {
    if (!blacklist[item + '._x_']) { out.push(item); }
  });

  return out
}

/**
 * A file within a unionFolder.
 */
var UnionFile = function UnionFile (master, fallback, whiteout) {
  this._master = master;
  this._fallback = fallback;
  this._whiteout = whiteout;
};

UnionFile.prototype.delete = function delete$1 () {
  return Promise.all([
    this._whiteout.setData([]),
    this._master.delete().catch(function (e) { return null; })
  ])
};

UnionFile.prototype.getData = function getData () {
    var this$1 = this;

  return this._master.getData().catch(function (e) { return this$1._whiteout.getData().then(
      function (data) {
        throw new Error('File has been deleted')
      },
      function (e) { return this$1._fallback.getData(); }
    ); }
  )
};

UnionFile.prototype.getText = function getText () {
    var this$1 = this;

  return this._master.getText().catch(function (e) { return this$1._whiteout.getData().then(
      function (data) {
        throw new Error('File has been deleted')
      },
      function (e) { return this$1._fallback.getText(); }
    ); }
  )
};

UnionFile.prototype.setData = function setData (data) {
  return this._master.setData(data)
};

UnionFile.prototype.setText = function setText (text) {
  return this._master.setText(text)
};

/**
 * Reads and writes go to a master folder, but if a read fails,
 * we will also try the fallback folder.
 */
var UnionFolder = function UnionFolder (master, fallback) {
  this._master = master;
  this._fallback = fallback;
};

UnionFolder.prototype.delete = function delete$2 () {
  return Promise.all([
    mapFiles(this, function (file) { return file.delete(); }),
    mapFolders(this, function (folder) { return folder.delete(); })
  ]).then(function () {})
};

UnionFolder.prototype.file = function file (name) {
  return new UnionFile(
    this._master.file(name),
    this._fallback.file(name),
    this._master.file(name + '._x_')
  )
};

UnionFolder.prototype.folder = function folder (name) {
  return new UnionFolder(
    this._master.folder(name),
    this._fallback.folder(name)
  )
};

UnionFolder.prototype.listFiles = function listFiles () {
  return Promise.all([
    this._master.listFiles(),
    this._fallback.listFiles()
  ]).then(function (values) { return removeDuplicates(values[0], values[1]); })
};

UnionFolder.prototype.listFolders = function listFolders () {
  return Promise.all([
    this._master.listFolders(),
    this._fallback.listFolders()
  ]).then(function (values) { return removeDuplicates(values[0], values[1]); })
};

function makeUnionFolder$$1 (master, fallback) {
  return new UnionFolder(master, fallback)
}

export { locateFile, locateFolder, mapAllFiles, mapFiles, mapFolders, makeLocalStorageFolder, makeLoggedFolder, makeMemoryFolder, makeNodeFolder, makeUnionFolder$$1 as makeUnionFolder };
//# sourceMappingURL=disklet.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/disklet/lib/disklet.js
// module id = 1224
// module chunks = 157